import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ExternalLink, ImagePlus, Loader2, Plus, X } from 'lucide-react';
import {
  fetchPosts, createPost, savePost, deletePost, previewPost, saveBlog, uploadPostImage, siteDownloadUrl,
  type Site, type SiteBlog, type SitePost, type SiteRedirect,
} from '@/services/siteApi';

// Posts on /my-site: write a blog post, preview it in the site's own look, publish
// it to the live site, and change any address without breaking old links.
// Server: sitePosts.js. Same look as the other MySitePage panels.

const INK = 'var(--hj-ink)';
const MUTED = 'var(--hj-ink-2)';
const LIVE_BG = '#e3f1d3', LIVE_FG = '#2f5d1a';
const panel: React.CSSProperties = { background: '#fff', border: '1px solid var(--hj-hairline-soft)', borderRadius: 16, padding: 24 };
const h2: React.CSSProperties = { margin: 0, fontFamily: 'var(--hj-font-display)', fontWeight: 800, fontSize: 22, letterSpacing: '-0.01em', color: INK };
const small: React.CSSProperties = { fontSize: 14, color: MUTED, margin: 0, lineHeight: 1.45 };
const errText: React.CSSProperties = { ...small, color: 'var(--hj-pink-ink)' };
const label: React.CSSProperties = { fontWeight: 600, fontSize: 14, color: INK };
const input: React.CSSProperties = { width: '100%', minWidth: 0, minHeight: 44, border: '1px solid var(--hj-hairline)', borderRadius: 10, padding: '0 12px', font: '15px var(--hj-font-ui)', color: INK, background: '#fff', boxSizing: 'border-box' };
const btnBase: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 44, padding: '10px 18px', borderRadius: 12, fontFamily: 'inherit', fontWeight: 600, fontSize: 15, cursor: 'pointer', boxSizing: 'border-box', textDecoration: 'none' };
const primary: React.CSSProperties = { ...btnBase, background: INK, color: '#fff', border: 0 };
const quiet: React.CSSProperties = { ...btnBase, minHeight: 40, padding: '8px 14px', fontSize: 14, fontWeight: 500, background: '#fff', color: INK, border: '1px solid var(--hj-hairline)', borderRadius: 10 };
const linkBtn: React.CSSProperties = { background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: INK, textDecoration: 'underline', cursor: 'pointer', minHeight: 32 };
const off = (s: React.CSSProperties, o: boolean): React.CSSProperties => (o ? { ...s, opacity: 0.45, cursor: 'not-allowed' } : s);
const pill = (bg: string, fg: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', background: bg, color: fg, fontWeight: 600, fontSize: 12, padding: '3px 9px', borderRadius: 999 });
const day = (d: string | null) => (d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '');
const slugify = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80);

type View = { kind: 'list' } | { kind: 'edit'; post: SitePost | null } | { kind: 'settings' };

// What a coding agent needs to make the blog match the site: two template files the
// upload can carry, with the placeholders sitePosts.js fills. Kept in one place so the
// "build me a site" prompt and the Posts panel say the same thing.
export const BLOG_TEMPLATE_ASK = 'Also make two template files in the same look as the site, for the blog Hey Joe Sites adds: _post.html for one post, using the placeholders {{title}}, {{date}}, {{cover}} and {{content}} in the page and {{blog_title}} and {{blog_url}} for a link back to the blog ({{description}} and {{url}} are available for the head tags); and _blog.html for the list of posts, using {{blog_title}}, {{blog_url}} and {{posts}}, where {{posts}} is a <ul class="post-list"> with an h2 link, a .date div, a p excerpt and sometimes an img per item, so style those. Leave every placeholder exactly as written; Hey Joe fills them in. Put both files at the top of the zip, next to index.html.';
export const MATCH_SITE_PROMPT = `Here are my website’s files. ${BLOG_TEMPLATE_ASK} Give me back the whole site as a zip.`;

const MatchYourSite: React.FC<{ site: Site }> = ({ site }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!open) return <button type="button" onClick={() => setOpen(true)} style={{ ...linkBtn, fontWeight: 500, alignSelf: 'flex-start' }}>Make the blog match my site</button>;
  return (
    <div className="flex flex-col" style={{ background: 'var(--hj-surface-1)', borderRadius: 12, padding: 14, gap: 10 }}>
      <p style={{ ...small, color: INK }}>Posts come in a clean default look. To make them match your site, add two small template files to your upload. Whatever built your site can make them: download your site’s zip below, give it this prompt along with the zip, and upload what comes back.</p>
      <span style={{ background: '#fff', border: '1px solid var(--hj-hairline-soft)', borderRadius: 10, padding: '10px 12px', fontSize: 13, color: INK, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{MATCH_SITE_PROMPT}</span>
      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <button type="button" style={quiet} onClick={() => { navigator.clipboard?.writeText(MATCH_SITE_PROMPT).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); }).catch(() => {}); }}>{copied ? 'Copied' : 'Copy the prompt'}</button>
        {site.liveVersionId && <a href={`/api/sites/${site.id}/download`} style={{ ...linkBtn, fontWeight: 500 }} onClick={(e) => { e.preventDefault(); siteDownloadUrl(site.id).then((r) => { window.location.href = r.url; }).catch(() => {}); }}>Download my site’s zip</a>}
        <button type="button" onClick={() => setOpen(false)} style={{ ...linkBtn, fontWeight: 500, color: MUTED }}>Close</button>
      </div>
    </div>
  );
};

const SitePostsPanel: React.FC<{ site: Site }> = ({ site }) => {
  const [blog, setBlog] = useState<SiteBlog | null>(null);
  const [posts, setPosts] = useState<SitePost[]>([]);
  const [view, setView] = useState<View>({ kind: 'list' });
  const [err, setErr] = useState<string | null>(null);
  const host = site.primaryHost || site.url.replace('https://', '');
  const load = () => fetchPosts(site.id).then((r) => { setBlog(r.blog); setPosts(r.posts); }).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, [site.id]);
  const isLive = site.status === 'live';

  if (!blog) return <section style={panel}><h2 style={h2}>Posts</h2>{err ? <p style={{ ...errText, marginTop: 8 }}>{err}</p> : <Loader2 className="w-5 h-5 animate-spin" style={{ marginTop: 12 }} />}</section>;
  if (view.kind === 'edit') return <PostEditor site={site} blog={blog} host={host} post={view.post} onDone={() => { setView({ kind: 'list' }); load(); }} />;
  if (view.kind === 'settings') return <BlogSettings site={site} blog={blog} host={host} onDone={(b) => { if (b) setBlog(b); setView({ kind: 'list' }); load(); }} />;

  return (
    <section id="posts" className="flex flex-col" style={{ ...panel, gap: 14 }}>
      <div className="flex flex-wrap items-center justify-between" style={{ gap: 12 }}>
        <h2 style={h2}>Posts</h2>
        <button type="button" onClick={() => setView({ kind: 'edit', post: null })} style={primary}><Plus size={18} aria-hidden="true" />New post</button>
      </div>
      <p style={small}>
        Your blog lives at {isLive ? <a href={`https://${host}/${blog.path}/`} target="_blank" rel="noreferrer" style={{ color: INK, fontWeight: 600, wordBreak: 'break-all' }}>{host}/{blog.path}/</a> : <strong style={{ color: INK, wordBreak: 'break-all' }}>{host}/{blog.path}/</strong>}.{' '}
        <button type="button" onClick={() => setView({ kind: 'settings' })} style={{ ...linkBtn, fontWeight: 500 }}>Blog settings and forwarding</button>
      </p>
      {!isLive && <p style={small}>Posts go live once your site is published. Until then they show on your preview address.</p>}
      <MatchYourSite site={site} />
      {err && <p style={errText}>{err}</p>}
      {posts.length === 0 ? (
        <p style={small}>No posts yet. Write one here, add photos or a video, and publish it to your site in one click.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {posts.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center justify-between" style={{ gap: 10, padding: '12px 0', borderTop: '1px solid var(--hj-hairline-soft)' }}>
              <div className="flex flex-col" style={{ minWidth: 0, flex: '1 1 240px', gap: 2 }}>
                <span style={{ fontWeight: 600, color: INK, wordBreak: 'break-word' }}>{p.title}</span>
                <span style={{ ...small, fontSize: 13, wordBreak: 'break-all' }}>{p.status === 'published' ? `${day(p.date)} · ${p.url}` : `Draft · edited ${day(p.updatedAt)}`}</span>
              </div>
              <div className="flex items-center" style={{ gap: 10 }}>
                {p.status === 'published' ? <span style={pill(LIVE_BG, LIVE_FG)}>Live</span> : <span style={pill('var(--hj-surface-2)', INK)}>Draft</span>}
                <button type="button" onClick={() => setView({ kind: 'edit', post: p })} style={quiet}>Edit</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const PostEditor: React.FC<{ site: Site; blog: SiteBlog; host: string; post: SitePost | null; onDone: () => void }> = ({ site, blog, host, post, onDone }) => {
  const [title, setTitle] = useState(post?.title || '');
  const [slug, setSlug] = useState(post?.slug || '');
  const [slugTouched, setSlugTouched] = useState(!!post);
  const [body, setBody] = useState(post?.body || '');
  const [cover, setCover] = useState<string | null>(post?.cover || null);
  const [excerpt, setExcerpt] = useState(post?.excerpt || '');
  const [date, setDate] = useState(post?.date ? post.date.slice(0, 10) : '');
  const [oldPaths, setOldPaths] = useState((post?.oldPaths || []).join('\n'));
  const [more, setMore] = useState(!!(post && (post.oldPaths.length || post.excerpt)));
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  const [html, setHtml] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState<SitePost | null>(post);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const coverRef = useRef<HTMLInputElement>(null);
  const live = saved?.status === 'published';
  const finalSlug = slugTouched ? slug : slugify(title);
  const moved = live && saved && finalSlug && finalSlug !== saved.slug;

  const draft = (publish?: boolean) => ({ title, slug: finalSlug || undefined, body, excerpt, cover, date: date || null,
    oldPaths: oldPaths.split('\n').map((s) => s.trim()).filter(Boolean), ...(publish === undefined ? {} : { publish }) });
  useEffect(() => {
    if (tab !== 'preview') return;
    previewPost(site.id, draft()).then((r) => setHtml(r.html)).catch((e) => setErr(e.message));
  }, [tab]);

  const run = async (what: string, fn: () => Promise<void>) => {
    setBusy(what); setErr(null);
    try { await fn(); } catch (e) { setErr((e as Error).message); }
    setBusy(null);
  };
  const save = (publish?: boolean) => run(publish ? 'publish' : publish === false ? 'unpublish' : 'save', async () => {
    const r = saved ? await savePost(site.id, saved.id, draft(publish)) : await createPost(site.id, draft(publish));
    setSaved(r.post); setSlug(r.post.slug); setSlugTouched(true); setOldPaths(r.post.oldPaths.join('\n'));
    if (r.post.date) setDate(r.post.date.slice(0, 10));
  });
  const insertImage = (file: File) => run('image', async () => {
    const { path } = await uploadPostImage(site.id, file);
    const el = bodyRef.current;
    const at = el ? el.selectionStart : body.length;
    const snippet = `\n\n![](${path})\n\n`;
    setBody((b) => (b.slice(0, at) + snippet + b.slice(at)).replace(/^\n+/, ''));
  });
  const busyIcon = (k: string, text: string) => (busy === k ? <Loader2 className="w-4 h-4 animate-spin" /> : text);

  return (
    <section className="flex flex-col" style={{ ...panel, gap: 16 }}>
      <div className="flex flex-wrap items-center justify-between" style={{ gap: 12 }}>
        <button type="button" onClick={onDone} style={{ ...linkBtn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}><ChevronLeft size={18} aria-hidden="true" />All posts</button>
        {live && saved && <a href={`https://${host}${saved.url}`} target="_blank" rel="noreferrer" style={{ ...quiet, minHeight: 36 }}>View post <ExternalLink size={14} aria-hidden="true" /></a>}
      </div>

      <div className="flex flex-col" style={{ gap: 6 }}>
        <label htmlFor="post-title" style={label}>Title</label>
        <input id="post-title" value={title} onChange={(e) => setTitle(e.target.value)} style={{ ...input, fontSize: 18, fontWeight: 600 }} maxLength={200} />
      </div>

      <div className="flex flex-col" style={{ gap: 6 }}>
        <label htmlFor="post-slug" style={label}>Address</label>
        <div className="flex flex-wrap items-center" style={{ gap: 4 }}>
          <span style={{ ...small, wordBreak: 'break-all' }}>{host}/{blog.path}/</span>
          <input id="post-slug" value={finalSlug} autoCapitalize="none" spellCheck={false} style={{ ...input, flex: '1 1 160px', width: 'auto' }}
            onChange={(e) => { setSlugTouched(true); setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-{2,}/g, '-')); }} />
        </div>
        {moved && <p style={small}>The old address, /{blog.path}/{saved!.slug}/, will forward here so links you’ve shared keep working.</p>}
      </div>

      <div className="flex flex-col" style={{ gap: 6 }}>
        <span style={label}>Cover image</span>
        {cover ? (
          <div className="flex flex-wrap items-center" style={{ gap: 12 }}>
            <img src={cover.startsWith('/') ? `${site.status === 'live' ? `https://${host}` : site.previewUrl || ''}${cover}` : cover} alt="" style={{ width: 120, height: 68, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--hj-hairline-soft)' }} />
            <button type="button" onClick={() => setCover(null)} style={{ ...linkBtn, fontWeight: 500 }}>Remove</button>
          </div>
        ) : (
          <div>
            <button type="button" onClick={() => coverRef.current?.click()} disabled={!!busy} style={off(quiet, !!busy)}>{busy === 'cover' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ImagePlus size={16} aria-hidden="true" />Add a cover</>}</button>
          </div>
        )}
        <input ref={coverRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden onChange={(e) => {
          const f = e.target.files?.[0]; e.target.value = '';
          if (f) run('cover', async () => { const { path } = await uploadPostImage(site.id, f); setCover(path); });
        }} />
      </div>

      <div className="flex flex-col" style={{ gap: 8 }}>
        <div className="flex flex-wrap items-center justify-between" style={{ gap: 8 }}>
          <div className="flex" role="tablist" style={{ gap: 4 }}>
            {(['write', 'preview'] as const).map((t) => (
              <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
                style={{ ...quiet, minHeight: 36, background: tab === t ? INK : '#fff', color: tab === t ? '#fff' : INK, border: tab === t ? 0 : quiet.border }}>{t === 'write' ? 'Write' : 'Preview'}</button>
            ))}
          </div>
          {tab === 'write' && <button type="button" onClick={() => fileRef.current?.click()} disabled={!!busy} style={off({ ...quiet, minHeight: 36 }, !!busy)}>{busy === 'image' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ImagePlus size={16} aria-hidden="true" />Add a photo</>}</button>}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) insertImage(f); }} />
        </div>
        {tab === 'write' ? (
          <>
            <textarea ref={bodyRef} aria-label="Post" value={body} onChange={(e) => setBody(e.target.value)} rows={16}
              style={{ ...input, minHeight: 320, padding: 12, lineHeight: 1.6, resize: 'vertical', fontFamily: 'var(--hj-font-ui)' }} />
            <p style={{ ...small, fontSize: 13 }}>**bold**, *italic*, [a link](https://…), # a heading, - a list. Paste a YouTube or Vimeo link on its own line to show the video.</p>
          </>
        ) : (
          <iframe title="Post preview" sandbox="allow-scripts allow-popups" srcDoc={html || '<p style="font-family:sans-serif;color:#888">Drawing the preview…</p>'}
            style={{ width: '100%', height: 560, border: '1px solid var(--hj-hairline-soft)', borderRadius: 12, background: '#fff' }} />
        )}
      </div>

      <div className="flex flex-col" style={{ gap: 8 }}>
        <button type="button" onClick={() => setMore(!more)} style={{ ...linkBtn, fontWeight: 500, alignSelf: 'flex-start' }}>{more ? 'Fewer options' : 'Date, summary and old addresses'}</button>
        {more && (
          <div className="flex flex-col" style={{ gap: 12 }}>
            <div className="flex flex-col" style={{ gap: 6 }}>
              <label htmlFor="post-date" style={label}>Date</label>
              <input id="post-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...input, width: 200 }} />
              <p style={small}>Set when it was first published, for a post you’re moving over. Left empty, it’s the day you publish.</p>
            </div>
            <div className="flex flex-col" style={{ gap: 6 }}>
              <label htmlFor="post-excerpt" style={label}>Summary</label>
              <textarea id="post-excerpt" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={2} maxLength={300} style={{ ...input, padding: 10, minHeight: 64 }} />
              <p style={small}>Shows on the blog page and in link previews. Left empty, we use the first lines of the post.</p>
            </div>
            <div className="flex flex-col" style={{ gap: 6 }}>
              <label htmlFor="post-old" style={label}>Old addresses</label>
              <textarea id="post-old" value={oldPaths} onChange={(e) => setOldPaths(e.target.value)} rows={3} placeholder="/2019/06/my-post/" spellCheck={false} style={{ ...input, padding: 10, minHeight: 72, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 14 }} />
              <p style={small}>One per line. If this post lived at another address before, like on your old site, visitors who follow those links land here.</p>
            </div>
          </div>
        )}
      </div>

      {err && <p style={errText}>{err}</p>}
      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <button type="button" disabled={!!busy || !title.trim()} onClick={() => save(true)} style={off(primary, !!busy || !title.trim())}>{busyIcon('publish', live ? 'Update live post' : 'Publish')}</button>
        {!live && <button type="button" disabled={!!busy || !title.trim()} onClick={() => save(false)} style={off(quiet, !!busy || !title.trim())}>{busyIcon('unpublish', 'Save draft')}</button>}
        {live && <button type="button" disabled={!!busy} onClick={() => save(false)} style={off(quiet, !!busy)}>{busyIcon('unpublish', 'Unpublish')}</button>}
        {saved && !confirmDelete && <button type="button" onClick={() => setConfirmDelete(true)} style={{ ...linkBtn, fontWeight: 500, color: MUTED, marginLeft: 'auto' }}>Delete</button>}
        {saved && confirmDelete && (
          <span className="flex items-center" style={{ gap: 8, marginLeft: 'auto' }}>
            <span style={small}>Delete this post for good?</span>
            <button type="button" disabled={!!busy} onClick={() => run('delete', async () => { await deletePost(site.id, saved.id); onDone(); })}
              style={{ ...quiet, background: 'hsl(var(--destructive))', color: 'hsl(var(--destructive-foreground))', border: 0 }}>{busyIcon('delete', 'Delete')}</button>
            <button type="button" onClick={() => setConfirmDelete(false)} style={quiet}>Keep</button>
          </span>
        )}
      </div>
      {saved && !busy && !err && <p style={{ ...small, color: LIVE_FG }}>{live ? `Live at ${host}${saved.url}` : 'Saved as a draft. Only you can see it.'}</p>}
    </section>
  );
};

const BlogSettings: React.FC<{ site: Site; blog: SiteBlog; host: string; onDone: (b?: SiteBlog) => void }> = ({ site, blog, host, onDone }) => {
  const [title, setTitle] = useState(blog.title);
  const [path, setPath] = useState(blog.path);
  const [rows, setRows] = useState<SiteRedirect[]>(blog.redirects.length ? blog.redirects : [{ from: '', to: '' }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const setRow = (i: number, k: keyof SiteRedirect, v: string) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <section className="flex flex-col" style={{ ...panel, gap: 16 }}>
      <button type="button" onClick={() => onDone()} style={{ ...linkBtn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start' }}><ChevronLeft size={18} aria-hidden="true" />All posts</button>
      <h2 style={h2}>Blog settings</h2>
      <div className="flex flex-col" style={{ gap: 6 }}>
        <label htmlFor="blog-title" style={label}>Blog name</label>
        <input id="blog-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} style={input} />
      </div>
      <div className="flex flex-col" style={{ gap: 6 }}>
        <label htmlFor="blog-path" style={label}>Blog address</label>
        <div className="flex flex-wrap items-center" style={{ gap: 4 }}>
          <span style={{ ...small, wordBreak: 'break-all' }}>{host}/</span>
          <input id="blog-path" value={path} autoCapitalize="none" spellCheck={false} onChange={(e) => setPath(e.target.value.toLowerCase().replace(/[^a-z0-9/-]/g, ''))} style={{ ...input, flex: '1 1 140px', width: 'auto' }} />
          <span style={small}>/</span>
        </div>
        {path !== blog.path && <p style={small}>Everything at /{blog.path}/ will forward to /{path}/, so old links keep working.</p>}
      </div>
      <div className="flex flex-col" style={{ gap: 8 }}>
        <span style={label}>Forwarding</span>
        <p style={small}>Send an old address to a page on your site. Moving from another site? Add its old page addresses here, like /about-us/ to /about/.</p>
        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap items-center" style={{ gap: 6 }}>
            <input aria-label="Old address" placeholder="/old-page/" value={r.from} onChange={(e) => setRow(i, 'from', e.target.value)} spellCheck={false} autoCapitalize="none" style={{ ...input, flex: '1 1 150px', width: 'auto', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 14 }} />
            <span style={small}>to</span>
            <input aria-label="New address" placeholder="/new-page/" value={r.to} onChange={(e) => setRow(i, 'to', e.target.value)} spellCheck={false} autoCapitalize="none" style={{ ...input, flex: '1 1 150px', width: 'auto', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 14 }} />
            <button type="button" aria-label="Remove" onClick={() => setRows(rows.length > 1 ? rows.filter((_, j) => j !== i) : [{ from: '', to: '' }])} style={{ ...quiet, minHeight: 40, padding: '8px 10px' }}><X size={16} aria-hidden="true" /></button>
          </div>
        ))}
        <button type="button" onClick={() => setRows([...rows, { from: '', to: '' }])} style={{ ...linkBtn, fontWeight: 500, alignSelf: 'flex-start' }}>Add another</button>
      </div>
      {err && <p style={errText}>{err}</p>}
      <div className="flex flex-wrap" style={{ gap: 10 }}>
        <button type="button" disabled={busy} style={off(primary, busy)} onClick={async () => {
          setBusy(true); setErr(null);
          try { const r = await saveBlog(site.id, { title, path, redirects: rows.filter((x) => x.from.trim() || x.to.trim()) }); onDone(r.blog); } catch (e) { setErr((e as Error).message); setBusy(false); }
        }}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</button>
        <button type="button" disabled={busy} onClick={() => onDone()} style={quiet}>Cancel</button>
      </div>
    </section>
  );
};

export default SitePostsPanel;
